import { useMemo, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CATALOGUE, CATEGORY_LABEL, type CatalogueCategory, type CatalogueEntry } from './liveActivityCatalogue'
import { MAX_ACTIVITIES, TYPE_CHIP, type Draft } from './liveActivityDraft'

type Filter = 'all' | CatalogueCategory

function matches(e: CatalogueEntry, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [e.name, e.tagline, e.what, e.when, ...e.tags].join(' ').toLowerCase().includes(q)
}

function EntryCard({
  entry, added, atLimit, open, onToggle, onAdd,
}: {
  entry: CatalogueEntry; added: boolean; atLimit: boolean; open: boolean; onToggle: () => void; onAdd: () => void
}) {
  const Icon = entry.icon
  return (
    <li className={`rounded-xl border bg-white shadow-sm flex flex-col ${added ? 'border-gray-200 opacity-80' : 'border-gray-200'}`}>
      <div className="p-4 flex gap-3">
        <span className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${TYPE_CHIP[entry.type]}`}><Icon size={20} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900">{entry.name}</h3>
          <p className="text-sm text-gray-600 mt-0.5">{entry.tagline}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {entry.tags.map(t => <span key={t} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">{t}</span>)}
          </div>
        </div>
      </div>

      {open && (
        <dl id={`details-${entry.id}`} className="px-4 pb-4 space-y-3 text-sm">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">What it does</dt>
            <dd className="text-gray-700 mt-0.5">{entry.what}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">When to use it</dt>
            <dd className="text-gray-700 mt-0.5">{entry.when}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Example</dt>
            <dd className="mt-0.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-gray-700 space-y-0.5">
              {entry.example.map((line, i) => <p key={i}>{line}</p>)}
            </dd>
          </div>
          {entry.note && (
            <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">{entry.note}</div>
          )}
          {entry.preview && (
            <img src={entry.preview.src} alt={entry.preview.alt} className="rounded-lg border border-gray-200 w-full" loading="lazy" />
          )}
        </dl>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`details-${entry.id}`}
          className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 cursor-pointer"
        >
          {open ? 'Hide details' : 'Show details'}
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {added ? (
          <span className="inline-flex items-center gap-1 text-sm text-green-700"><Check size={14} /> Added</span>
        ) : (
          <Button type="button" size="sm" className="gap-1.5" disabled={atLimit} onClick={onAdd}>
            <Plus size={14} /> Add
          </Button>
        )}
      </div>
    </li>
  )
}

/** The "app store" for live activities: browse, search, read what each one is for, and add it to the course. */
export function AddActivityDialog({
  open, onOpenChange, drafts, onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  drafts: Draft[]
  onAdd: (entry: CatalogueEntry) => void
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [expanded, setExpanded] = useState<string | null>(null)

  const atLimit = drafts.length >= MAX_ACTIVITIES
  const isAdded = (e: CatalogueEntry) => e.role !== null && drafts.some(d => d.type === e.type && d.role === e.role)

  const shown = useMemo(
    () => CATALOGUE.filter(e => (filter === 'all' || e.category === filter) && matches(e, query)),
    [filter, query],
  )

  const handleOpenChange = (next: boolean) => {
    if (!next) { setQuery(''); setFilter('all'); setExpanded(null) }
    onOpenChange(next)
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(96vw,58rem)] max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl focus:outline-none"
          aria-describedby="add-activity-description"
        >
          <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900">Add a live activity</Dialog.Title>
              <Dialog.Description id="add-activity-description" className="text-sm text-gray-500 mt-0.5">
                Choose what you want to run during a session. You can edit it as soon as it&apos;s added.
              </Dialog.Description>
            </div>
            <Dialog.Close className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-pointer" aria-label="Close">
              <X size={18} />
            </Dialog.Close>
          </div>

          <div className="px-6 pb-3 space-y-3 border-b border-gray-100">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search activities, e.g. quiz, icebreaker, feedback"
                aria-label="Search activities"
                className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by purpose">
              {(['all', 'learning', 'engage', 'feedback'] as const).map(f => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  aria-pressed={filter === f}
                  className={`rounded-full px-3 py-1 text-sm font-medium cursor-pointer transition-colors ${
                    filter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'All' : CATEGORY_LABEL[f]}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            {atLimit && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                You&apos;ve reached the limit of {MAX_ACTIVITIES} activities for a course. Delete one to add another.
              </p>
            )}

            {shown.length === 0 ? (
              <p className="py-10 text-center text-sm text-gray-500">
                Nothing matches &ldquo;{query}&rdquo;.{' '}
                <button type="button" className="text-indigo-600 underline cursor-pointer" onClick={() => { setQuery(''); setFilter('all') }}>Clear the search</button>
              </p>
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2 items-start">
                {shown.map(e => (
                  <EntryCard
                    key={e.id}
                    entry={e}
                    added={isAdded(e)}
                    atLimit={atLimit}
                    open={expanded === e.id}
                    onToggle={() => setExpanded(x => (x === e.id ? null : e.id))}
                    onAdd={() => { onAdd(e); handleOpenChange(false) }}
                  />
                ))}
              </ul>
            )}
          </div>

          <p className="border-t border-gray-100 px-6 py-3 text-xs text-gray-500">
            Looking for polls? Trainers make <strong>quick polls</strong> on the spot during a session, from the live console.
            There&apos;s nothing to prepare here.
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
